import React, { PropsWithChildren, useRef } from "react";
import { Image } from "@wss/image";
import { useHomePageData } from "../utilities";
import { trackClickThroughStats } from "~/userAnalytics";
import { AdvertisementItem } from "../HomePageResult";
import { usePromotionTracking, handlePromotionClick } from "~/Gtm/usePromotionTracking";
type AdvertisementCardProps = AdvertisementItem & {
  index: number;
};
const AdvertisementCardHeader = ({
  link,
  id,
  altText,
  image,
  couponCode,
  index
}: AdvertisementCardProps) => {
  return <div className="relative group" data-sentry-component="AdvertisementCardHeader" data-sentry-source-file="index.tsx">
      <a className="gtm-promo block focus-visible:outline-[3px] focus-visible:outline focus-visible:outline-offset-[-2px] hover:[&:not(:focus-visible)]:opacity-90" href={link} data-id={id} data-ctt="2" data-ctd={id} data-position={index + 1} data-name={altText} onClick={e => {
      trackClickThroughStats(e);
      handlePromotionClick(e);
    }}>
        <Image className="w-full block h-auto" src={image} alt={altText} width={57} height={37} data-sentry-element="Image" data-sentry-source-file="index.tsx" />
      </a>
      {!!couponCode && typeof couponCode === "string" ? <div className="absolute bg-white font-semibold !leading-tight text-xs xsl:text-sm md:text-xs lt:text-sm rounded-t sm:rounded-t-lg md:rounded-t xxl:rounded-t-lg px-4 py-2 left-1/2 -translate-x-1/2 bottom-0 w-max">
          <span className="text-gray-900">
            <span className="hidden xs:inline md:hidden lt:inline">Use</span>{" "}
            Code:{" "}
          </span>
          <span className="text-green-600">
            {couponCode.toLocaleUpperCase()}
          </span>
        </div> : null}
    </div>;
};
const AdvertisementCardBody = ({
  headline,
  disclaimer,
  link,
  id,
  subHeader,
  index,
  altText
}: AdvertisementCardProps) => {
  return <div className="h-full" data-sentry-component="AdvertisementCardBody" data-sentry-source-file="index.tsx">
      <a className="group block h-full text-gray-900 w-full focus-visible:outline-[3px] focus-visible:outline focus-visible:outline-offset-[-2px]" href={link} data-id={id} data-ctt="2" data-ctd={id} data-position={index + 1} data-name={altText} onClick={e => {
      trackClickThroughStats(e);
      handlePromotionClick(e);
    }}>
        <div className="px-2 xsl:px-3 xs:px-4 md:px-2 lt:px-3 lz:px-4">
          <div className="group-hover:underline !leading-[1.1] line-clamp-2 text-ellipsis font-bold text-base xsl:text-lg md:text-base lt:text-lg xxxl:text-xl pt-3 xsl:pt-4 xs:pt-6 md:pt-3 lt:pt-4 lz:pt-6 [text-wrap:balance]">
            {headline}
          </div>
          <div className="!leading-tight line-clamp-2 text-ellipsis text-xs xsl:text-sm md:text-xs lt:text-sm xxxl:text-base pt-1.5 mb-3 xsl:mb-4 xs:mb-6 md:mb-3 lt:mb-4 lz:mb-6 [text-wrap:balance]">
            {subHeader}
          </div>
          {disclaimer && <div className="!leading-tight text-2xs md:text-[9px] lt:text-2xs pb-2 xs:-mt-2 md:mt-1 lt:mt-0 lz:-mt-2">
              {disclaimer}
            </div>}
        </div>
      </a>
    </div>;
};
const AdvertisementCard = ({
  children
}: PropsWithChildren) => {
  const containerRef = useRef<HTMLDivElement>(null);
  usePromotionTracking(containerRef);
  return <div className="border border-gray-200 border-solid rounded-normal overflow-hidden w-full h-full text-center bg-gray-50 flex flex-col" data-testid="advertisement-item" ref={containerRef} data-sentry-component="AdvertisementCard" data-sentry-source-file="index.tsx">
      {children}
    </div>;
};
const AdvertisementCardContainer = ({
  children
}: PropsWithChildren) => {
  return <div className="container flex items-center justify-center" data-sentry-component="AdvertisementCardContainer" data-sentry-source-file="index.tsx">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-[.6rem] lg:gap-4 items-start w-full xs:p-0" data-testid="advertisement-list">
        {children}
      </div>
    </div>;
};
const Advertisements = () => {
  const {
    advertisements
  } = useHomePageData();
  return advertisements.length ? <div className="pt-6 bg-[linear-gradient(transparent_50%,_white_50%)]" data-sentry-component="Advertisements" data-sentry-source-file="index.tsx">
      <AdvertisementCardContainer data-sentry-element="AdvertisementCardContainer" data-sentry-source-file="index.tsx">
        {advertisements.map((ad, index) => {
        return <AdvertisementCard key={ad.id}>
              <AdvertisementCardHeader {...ad} index={index} />
              <AdvertisementCardBody {...ad} index={index} />
            </AdvertisementCard>;
      })}
      </AdvertisementCardContainer>
    </div> : null;
};
export default Advertisements;